import { gql, useMutation } from '@apollo/client';
import { maybeRestErrorMessage } from 'modules/campaigns/editor/utils';
import { DEFAULT_RESPONSE_ERROR_MESSAGE } from 'modules/campaigns/lib/constants';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { DestinationsFormValues, useDestinationsContext } from '../context';
import { getWebhookUpsertVariables } from '../utils';

export const addWebhookMutation = gql`
  mutation CreateCampaignAlertsWebhookRequest(
    $name: String!
    $enabled: Boolean!
    $endpoint: String!
    $authType: WebhookAuthType!
    $authToken: String
    $clientId: String
    $clientSecret: String
    $tokenEndpoint: String
  ) {
    createCampaignAlertsWebhook(
      name: $name
      enabled: $enabled
      endpoint: $endpoint
      authType: $authType
      authToken: $authToken
      clientId: $clientId
      clientSecret: $clientSecret
      tokenEndpoint: $tokenEndpoint
    ) {
      id
      createdAt
      updatedAt
      name
      enabled
      endpoint
      authType
      authToken
      clientId
      clientSecret
      tokenEndpoint
    }
  }
`;

export const useAddWebhook = (onComplete?: () => void) => {
  const dispatch = useDispatch();
  const {
    savedWebhooks: { refetch },
  } = useDestinationsContext();

  const [mutate, { data, loading: isLoading, error }] = useMutation(addWebhookMutation, {
    onCompleted: (response) => {
      const { name } = response?.createCampaignAlertsWebhook;
      if (name) {
        dispatch(addPositiveToast(`${name} added successfully.`));
        onComplete?.();
        refetch();
      }
    },
    onError: (e) => {
      const errorResponseMessage = maybeRestErrorMessage(e) || DEFAULT_RESPONSE_ERROR_MESSAGE;
      const errorMessage = `Cannot add webhook: ${errorResponseMessage}`;
      dispatch(addNegativeToastWithErrorId(errorMessage, 'useAddWebhook', e));
    },
  });

  const addWebhook = (formValues: DestinationsFormValues) => {
    const variables = getWebhookUpsertVariables({ formValues });
    mutate({ variables });
  };

  return {
    addWebhook,
    data,
    isLoading,
    errorMessage: error?.message || data?.createCampaignAlertsWebhook?.failure?.message,
  };
};
